import React from "react"
import type { StepCondition } from "./types"
import { INPUT_CLASS, OPERATOR_OPTIONS } from "./constants"
import { PlusIcon, XIcon } from "./icons"

interface StepConditionsEditorProps {
  conditions: StepCondition[]
  onChange: (conditions: StepCondition[]) => void
  disabled?: boolean
}

const newCondition = (): StepCondition => ({
  path: "/request/method",
  operator: "equals",
  value: "POST",
})

// Conditions deliberately use JSON Pointers rather than the action trigger
// condition fields: they can read the original bounded context or earlier
// step results, but never secrets.
export function StepConditionsEditor({
  conditions,
  onChange,
  disabled = false,
}: StepConditionsEditorProps) {
  const update = (index: number, updates: Partial<StepCondition>) => {
    onChange(conditions.map((condition, i) => i === index ? { ...condition, ...updates } : condition))
  }

  return (
    <div className="rounded-md border border-gray-100 bg-gray-50 p-2.5">
      <div className="mb-2 flex items-start justify-between gap-3">
        <div>
          <h4 className="text-xs font-medium text-gray-700">Run only when</h4>
          <p className="mt-0.5 text-xs text-gray-500">
            All conditions must match. Use JSON Pointers such as /request/method or /steps/step_key/output/id. Secrets cannot be read.
          </p>
        </div>
        <button
          type="button"
          disabled={disabled}
          onClick={() => onChange([...conditions, newCondition()])}
          className="inline-flex shrink-0 items-center px-2 py-1 text-xs font-medium text-indigo-600 hover:text-indigo-800 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <PlusIcon />
          <span className="ml-0.5">Add condition</span>
        </button>
      </div>
      {conditions.length === 0 ? (
        <p className="py-1 text-xs text-gray-400">This step runs whenever it is reached.</p>
      ) : (
        <div className="space-y-2">
          {conditions.map((condition, index) => (
            <div className="flex items-center gap-2" key={index}>
              <input
                aria-label={`Step condition ${index + 1} path`}
                value={condition.path}
                disabled={disabled}
                onChange={(event) => update(index, { path: event.target.value })}
                className={`min-w-0 flex-[2] ${INPUT_CLASS}`}
                placeholder="/request/method"
              />
              <select
                aria-label={`Step condition ${index + 1} operator`}
                value={condition.operator}
                disabled={disabled}
                onChange={(event) => update(index, { operator: event.target.value })}
                className={`min-w-0 flex-1 ${INPUT_CLASS}`}
              >
                {OPERATOR_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
              <input
                aria-label={`Step condition ${index + 1} value`}
                value={condition.value == null ? "" : String(condition.value)}
                disabled={disabled}
                onChange={(event) => update(index, { value: event.target.value })}
                className={`min-w-0 flex-1 ${INPUT_CLASS}`}
                placeholder="Value"
              />
              <button
                type="button"
                disabled={disabled}
                onClick={() => onChange(conditions.filter((_, i) => i !== index))}
                className="p-1 text-gray-400 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-50"
                aria-label="Remove step condition"
              >
                <XIcon size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
